import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { useField } from 'react-final-form';
import { CheckBoxField, DateField, FlexColumn, FormModal, InputField, SelectField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useLeaveContracts from '@ubeya/shared/hooks/account/useLeaveContracts';
import { isValueBetweenNumberRange, required } from '@ubeya/shared/utils/form';
import moment from 'moment';
import { formatBalanceLine } from '../leaveBalanceUtils';

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 28px;
`;

const WarningBanner = styled.div`
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.yellowLight};
  border: 1px solid ${({ theme }) => theme.colors.yellowPartially};
  border-radius: 4px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.black};
`;

const WarningTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const formatDate = (value) => (value ? moment(value).format(API_DATE_FORMAT) : undefined);

// Future transitionDate would write payout / forfeit / new-assignment events with
// occurredAt > today and break the "events anchor to the assignment window" invariant.
const isFutureDay = (day) => day && moment(day).startOf('day').isAfter(moment().startOf('day'));

const UnsettledBalanceWarning = ({ management }) => {
  const { t } = useContext(ConfigContext);
  const { input } = useField('payoutPreviousRemainder', { subscription: { value: true } });

  const positiveBalances = useMemo(
    () => (management?.balances || []).filter((balance) => Number(balance.balance) > 0),
    [management]
  );

  if (input.value || positiveBalances.length === 0) return null;

  return (
    <WarningBanner>
      <WarningTitle>{t('changeContract_unsettledBalance_title')}</WarningTitle>
      <div>
        {t('changeContract_unsettledBalance_body', {
          balances: positiveBalances.map((balance) => formatBalanceLine(balance, t)).join(', ')
        })}
      </div>
    </WarningBanner>
  );
};

const ChangeContractModal = ({ onClose, changeContract, management, employeeBranches = [] }) => {
  const { t } = useContext(ConfigContext);
  const { leaveContracts, isLoading } = useLeaveContracts();
  const { branchesOptions } = useBranches();

  const currentContractId = management?.accountContractV2Id;

  const contractOptions = useMemo(
    () =>
      (leaveContracts || [])
        .filter((contract) => contract.isActive !== false && contract.id !== currentContractId)
        .map((contract) => ({ label: contract.name, value: contract.id })),
    [leaveContracts, currentContractId]
  );

  // BE resolves branchId arbitrarily on multi-branch employees — scope here so payouts post deterministically.
  const branchPickerOptions = useMemo(() => {
    if (!employeeBranches?.length) return branchesOptions;
    const employeeBranchesSet = new Set(employeeBranches);
    return branchesOptions.filter(({ value }) => employeeBranchesSet.has(value));
  }, [branchesOptions, employeeBranches]);

  const initialValues = useMemo(
    () => ({
      transitionDate: moment().format(API_DATE_FORMAT),
      employmentRate: management?.employmentRate,
      payoutPreviousRemainder: false,
      branchId: management?.primaryBranchId ?? employeeBranches?.[0] ?? undefined
    }),
    [management, employeeBranches]
  );

  const onSubmit = useCallback(
    async (values) => {
      try {
        await changeContract({
          accountContractV2Id: values.accountContractV2Id,
          transitionDate: formatDate(values.transitionDate),
          employmentRate:
            Number.isFinite(Number(values.employmentRate)) && values.employmentRate !== ''
              ? Number(values.employmentRate)
              : undefined,
          payoutPreviousRemainder: !!values.payoutPreviousRemainder,
          branchId: values.branchId
        });
        onClose();
      } catch {
        // useOptimisticMutation already shows the toaster.
      }
    },
    [changeContract, onClose]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      initialValues={initialValues}
      size="small"
      requiredWarningFooter={t('missingRequiredFields')}
      onClose={onClose}
      title={t('changeLeaveContract')}
      confirmText={t('change')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnPristine={false}>
      <Container>
        <SelectField
          name="accountContractV2Id"
          title={t('targetLeaveContract')}
          options={contractOptions}
          validate={required}
          required
          usePortal
          isMulti={false}
          clearable
          isLoading={isLoading}
        />
        <DateField
          name="transitionDate"
          title={t('transitionDate')}
          validate={required}
          required
          usePortal
          isOutsideRange={isFutureDay}
        />
        <InputField
          name="employmentRate"
          title={`${t('employmentRate')} (0-100)`}
          type="number"
          validate={(value) => isValueBetweenNumberRange({ value, min: 0, max: 100, isRequired: false })}
        />
        <SelectField
          name="branchId"
          title={t('changeContract_branch')}
          options={branchPickerOptions}
          usePortal
          isMulti={false}
          clearable
        />
        <CheckBoxField name="payoutPreviousRemainder" title={t('payoutPreviousRemainder')} />
        <UnsettledBalanceWarning management={management} />
      </Container>
    </FormModal>
  );
};

export default ChangeContractModal;
